import { ref } from 'vue'
import { getI18n, getLocalizedEnumItems, getTranslatedItems } from '@/plugins/i18n'
import { defineStore } from 'pinia'

import { planningApi } from '@/apis/planning.api'

import { useFetchAndStoreAsync } from '@/composables/store'
import { entityChangeTypeEnum, usePortalStore } from '@/stores/portal.store'
import { withEntityChange } from '@/composables/entity-changes'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useArchive } from '@/composables/archive'
import { textColor } from '@/lib/utils'
import { buildTagPayload } from '@/composables/tag'
import { ABSENCE_COLOR } from '@/stores/schedule.store'

export const initPlanningStoreAsync = async() => {}

export const VACATION_PROJECT_ID = -1

export const usePlanningStore = defineStore('planning', () => {
  const api = planningApi
  const portalStore = usePortalStore()

  const companies = ref([])
  const equipments = ref([])
  const groups = ref([])
  const listingItems = ref([])
  const listings = ref([])
  const serviceTags = ref([])
  const projectTags = ref([])
  const projects = ref([])
  const staff = ref([])

  const projectTagKindEnum = Object.freeze({ GENERIC: 0, PROGRAM: 1 })
  const equipmentConditionEnum = Object.freeze({
    BROKEN: 0,
    NEEDS_REPAIR: 1,
    NEEDS_CLEANING: 2,
    GOOD: 3,
    GREAT: 4,
  })

  const roiComparisonTypeEnum = Object.freeze({
    EQUIPMENT: 0,
    COMPANY: 1,
    STAFF: 2,
  })

  const roiMetricFormatEnum = Object.freeze({
    MONEY: 0,
    PERCENT: 1,
    NUMBER: 2,
    INTEGER: 3,
    DATE: 4,
  })

  const roiMetricBetterEnum = Object.freeze({
    NONE: 0,
    HIGHER: 1,
    LOWER: 2,
  })

  const roiBarToneEnum = Object.freeze({
    MUTED: 0,
    BEST: 1,
    NEGATIVE: 2,
  })

  const roiComparisonEditStepEnum = Object.freeze({
    TYPE: 1,
    ITEMS: 2,
    REFERENCE: 3,
  })

  const roiCompareBasket = ref(Object.fromEntries(Object.values(roiComparisonTypeEnum).map(type => [type, []])))

  const equipmentConditionEnumLabels = [
    'common.condition.broken',
    'common.condition.needsRepair',
    'common.condition.needsCleaning',
    'common.condition.good',
    'common.condition.great',
  ]

  const equipmentConditionColors = ['negative', 'red', 'warning', 'positive', 'green', 'grey']

  const serviceImportTargetEnum = Object.freeze({
    SKIP: 0,
    IMPORTED_DATA: 1,
    CODE: 2,
    NAME: 3,
    DESCRIPTION: 4,
    QUANTITY: 5,
    RENTAL_PRICE: 6,
    PURCHASE_PRICE: 7,
    UNIT_PURCHASE_PRICE: 8,
    TAG: 9,
    SERIAL_NUMBER: 10,
    INTERNAL_SERIAL: 11,
    BUY_DATE: 12,
    BARCODE: 13,
    ACTIVE: 14,
    GROUP_CODE: 15,
    FIRST_NAME: 16,
    FULL_NAME: 17,
    EMAIL: 18,
    PHONE: 19,
    WAREHOUSE: 20,
    GROUP_NAME: 21,
    ADDRESS: 22,
  })

  const serviceImportTargetEnumLabels = [
    'common.equipmentImport.target.skip',
    'common.equipmentImport.target.importedData',
    'common.code',
    'common.name',
    'common.description',
    'common.quantity',
    'common.equipmentImport.target.rentalPrice',
    'common.equipmentImport.target.purchasePrice',
    'common.equipmentImport.target.unitPurchasePrice',
    'common.tag',
    'common.serialNumber',
    'common.internalSerial',
    'common.buyDate',
    'common.barcode',
    'common.active',
    'common.equipmentImport.target.groupCode',
    'common.firstName',
    'common.equipmentImport.target.fullName',
    'common.email',
    'common.phone',
    'common.warehouse',
    'common.equipmentImport.target.groupName',
    'common.equipmentImport.target.address',
  ]

  const staffImportTargetLabelOverrides = Object.freeze({
    [serviceImportTargetEnum.NAME]: 'common.lastName',
    [serviceImportTargetEnum.RENTAL_PRICE]: 'common.price',
    [serviceImportTargetEnum.PURCHASE_PRICE]: 'common.cost',
    [serviceImportTargetEnum.WAREHOUSE]: 'common.workplace',
  })

  const serviceImportMultiColumnTargets = Object.freeze([
    serviceImportTargetEnum.SKIP,
    serviceImportTargetEnum.IMPORTED_DATA,
    serviceImportTargetEnum.TAG,
    serviceImportTargetEnum.ADDRESS,
  ])

  const serviceImportCodeCollisionModeEnum = Object.freeze({
    SKIP: 0,
    NEW_CODE: 1,
  })

  const serviceImportNumberFormatExamples = ['1,234.56', '1.234,56', '1 234,56', '1\'234.56']
  const serviceImportDateFormatExamples = ['31/12/2026', '12/31/2026']

  const eventWarningTypeEnum = Object.freeze({
    OVERLAP: 0,
  })

  const projectStatusEnum = Object.freeze({
    PLANNED: 0,
    PRE_PRODUCTION: 1,
    PRODUCTION: 2,
    POST_PRODUCTION: 3,
    COMPLETED: 4,
  })

  const referenceTypeEnum = Object.freeze({
    PHONE: 0,
    EMAIL: 1,
    SOCIAL: 2,
  })

  const contactResolveResultEnum = Object.freeze({
    UNCHANGED: 0,
    CREATED: 1,
    UPDATED: 2,
  })

  const conversationTypeEnum = Object.freeze({
    PHONE: 0,
    EMAIL: 1,
    TEXT: 2,
    IN_PERSON: 3,
  })

  const projectDeleteModeEnum = Object.freeze({
    PROJECT_AND_QUOTE: 'project_and_quote',
    PROJECT_ONLY: 'project_only',
    ALLOCATIONS_ONLY: 'allocations_only',
  })

  const warehouseArchiveModeEnum = Object.freeze({
    ZERO_AND_ARCHIVE: 'zero_and_archive',
  })

  const projectBoundsActionEnum = Object.freeze({
    EXTEND: 0,
    TRIM: 1,
  })

  const projectAllocationActionEnum = Object.freeze({
    KEEP: 0,
    REDISTRIBUTE: 1,
  })

  const projectEditStepEnum = Object.freeze({
    BASIC_INFO: 1,
    DATE_TIME: 2,
    SERVICES: 3,
    ATTACHMENTS: 4,
  })

  const projectStatusColorEnum = Object.freeze({
    PLANNED: 'blue',
    PRE_PRODUCTION: 'teal',
    PRODUCTION: 'orange',
    POST_PRODUCTION: 'purple',
    COMPLETED: 'green',
  })

  const projectStatusEnumLabels = [
    'common.projectStatus.planned',
    'common.projectStatus.preProduction',
    'common.projectStatus.production',
    'common.projectStatus.postProduction',
    'common.projectStatus.completed',
  ]

  const warehouseKindEnum = Object.freeze({
    ALL: 0,
    PHYSICAL: 1,
    VIRTUAL: 2,
  })

  const warehouseMovementTypeEnum = Object.freeze({
    LOAD: 0,
    UNLOAD: 1,
    ADJUSTMENT: 2,
    TRANSFER: 3,
  })

  const warehouseMovementTypeLabels = [
    'common.warehouse.load',
    'common.warehouse.unload',
    'common.warehouse.adjustment',
    'common.warehouse.transfer',
  ]

  const warehouseAvailabilityStatusEnum = Object.freeze({
    OK: 0,
    BELOW_MIN: 1,
    SHORTAGE: 2,
  })

  const warehouseMovementStatusEnum = Object.freeze({
    SCHEDULED: 0,
    CONFIRMED: 1,
    CANCELLED: 2,
  })

  const warehouseMovementStatusLabels = [
    'common.warehouse.scheduled',
    'common.warehouse.confirmed',
    'common.warehouse.cancelled',
  ]

  const getLocalizedWarehouseMovementTypes = () => warehouseMovementTypeLabels.map((label, idx) => {
    const i18n = getI18n()
    return { id: idx, name: i18n.t(label), label }
  })

  const getLocalizedWarehouseMovementStatuses = () => warehouseMovementStatusLabels.map((label, idx) => {
    const i18n = getI18n()
    return { id: idx, name: i18n.t(label), label }
  })

  const staffMovementTypeLabels = [
    'common.staff.availability',
    'common.staff.absence',
    'common.staff.adjustment',
    'common.staff.transfer',
  ]

  const getLocalizedStaffMovementTypes = () => staffMovementTypeLabels.map((label, idx) => {
    const i18n = getI18n()
    return { id: idx, name: i18n.t(label), label }
  })

  const getVacationProject = (serviceIds) => {
    const i18n = getI18n()
    return {
      id: VACATION_PROJECT_ID,
      name: i18n.t('common.vacation'),
      color: ABSENCE_COLOR,
      tags: [],
      visible: true,
      services: serviceIds
    }
  }

  const isVacationProject = (id) => id == null || +id === VACATION_PROJECT_ID
  const isUnitaryService = (service) => service.type === portalStore.serviceTypeEnum.STAFF || service.is_serialized

  const addServiceTagAsync = async(tag) => api.addServiceTagAsync(tag)
  const fetchServiceTagsAsync = async(params, force = false) => useFetchAndStoreAsync(serviceTags, () => api.getServiceTagsAsync(params), force)
  const getLocalizedServiceTags = () => getTranslatedItems(serviceTags.value).sort((a, b) => a.name?.localeCompare(b.name))

  const addProjectTagAsync = async(tag) => api.addProjectTagAsync(tag)
  const fetchProjectTagsAsync = async(params, force = false) => useFetchAndStoreAsync(projectTags, () => api.getProjectTagsAsync(params), force)
  const createProjectTagAsync = async(tag) => {
    const created = (await addProjectTagAsync(buildTagPayload(tag.name, { kind: tag.kind }))).data
    await fetchProjectTagsAsync({}, true)
    return created.id
  }
  const getLocalizedProjectTagKinds = () => getLocalizedEnumItems(projectTagKindEnum, 'common.projectTagKinds')
  const getLocalizedProjectTags = () => getTranslatedItems(projectTags.value).sort((a, b) => a.name?.localeCompare(b.name))

  const archiveCompanyAsync = async(id) => api.archiveCompanyAsync(id)
  const archiveEquipmentAsync = async(id, config) => api.archiveEquipmentAsync(id, config)
  const archiveGroupAsync = async(id, config) => api.archiveGroupAsync(id, config)
  const archiveListingAsync = async(id) => api.archiveListingAsync(id)
  const archiveListingItemAsync = async(id) => api.archiveListingItemAsync(id)
  const archiveProjectAsync = async(id) => api.archiveProjectAsync(id)
  const archiveStaffAsync = async(id, config) => api.archiveStaffAsync(id, config)
  const resendInviteStaffAsync = async(id) => api.resendInviteStaffAsync(id)

  const cloneEquipmentAsync = async(id) => api.cloneEquipmentAsync(id)

  const getServiceResourceUrl = (serviceType) => serviceType === portalStore.serviceTypeEnum.STAFF ? api.staffsUrl : api.equipmentsUrl
  const analyzeServiceImportAsync = async(serviceType, data) => api.postAsync(`${getServiceResourceUrl(serviceType)}import-analyze/`, data)
  const importServicesAsync = async(serviceType, data) => api.postAsync(`${getServiceResourceUrl(serviceType)}import/`, data)
  const exportServicesExcelAsync = async(serviceType, ids) => api.exportServicesExcelAsync(
    getServiceResourceUrl(serviceType),
    serviceType === portalStore.serviceTypeEnum.STAFF ? 'staff.xlsx' : 'equipment.xlsx',
    ids,
  )
  const bulkDeleteServicesAsync = async(serviceType, data, config) => api.postAsync(`${getServiceResourceUrl(serviceType)}bulk-delete/`, data, config)
  const cloneGroupAsync = async(id) => api.cloneGroupAsync(id)
  const cloneListingAsync = async(id, data) => api.cloneListingAsync(id, data)
  const cloneListingItemAsync = async(id) => api.cloneListingItemAsync(id)

  const deleteCompanyAsync = async(id, config) => api.deleteCompanyAsync(id, config)
  const deleteEquipmentAsync = async(id, config) => api.deleteEquipmentAsync(id, config)
  const deleteGroupAsync = async(id, config) => api.deleteGroupAsync(id, config)
  const deleteListingAsync = async(id, config) => api.deleteListingAsync(id, config)
  const deleteListingItemAsync = async(id, config) => api.deleteListingItemAsync(id, config)
  const deleteProjectAsync = async(id, config) => api.deleteProjectAsync(id, config)
  const deleteProjectListingItemAsync = async(id) => api.deleteProjectListingItemAsync(id)
  const deleteStaffAsync = async(id, config) => api.deleteStaffAsync(id, config)
  const deleteEquipmentConditionAsync = async(id, config) => api.deleteEquipmentConditionAsync(id, config)

  const downloadProjectFileAsync = async(projectId, filename) => api.downloadProjectFileAsync(projectId, filename)
  const downloadServiceFileAsync = async(id, filename) => api.downloadServiceFileAsync(id, filename)
  const printProjectDeliveryNoteAsync = async(id, fileName, params) => api.printProjectDeliveryNoteAsync(id, fileName, params)
  const previewProjectDeliveryNoteAsync = async(id, params) => api.previewProjectDeliveryNoteAsync(id, params)

  const exportListingsAsync = async(params) => api.exportListingsAsync(params)
  const exportProjectsExcelAsync = async(ids, templateId, fileName, groupByPrimaryBudgetTags = false) => api.exportProjectsExcelAsync(ids, templateId, fileName, groupByPrimaryBudgetTags)

  const fetchCompaniesAsync = async(params, store = false) => useFetchAndStoreAsync(companies, () => api.getCompaniesAsync(params), store)
  const fetchCompanyByIdAsync = async(id, params = {}) => api.getCompanyByIdAsync(id, params)
  const fetchCompanyStatisticsByIdAsync = async(id, params = {}) => api.getCompanyStatisticsByIdAsync(id, params)
  const fetchCompanyRoiByIdAsync = async(id) => api.getCompanyRoiByIdAsync(id)
  const getRoiComparisonResourceUrl = (type) => ({
    [roiComparisonTypeEnum.EQUIPMENT]: api.equipmentsUrl,
    [roiComparisonTypeEnum.COMPANY]: api.companiesUrl,
    [roiComparisonTypeEnum.STAFF]: api.staffsUrl,
  })[type]
  const fetchRoiComparisonAsync = async(type, ids) => api.getAsync(`${getRoiComparisonResourceUrl(type)}roi-compare/`, { ids: ids.join(',') })
  const fetchRoiComparisonItemsAsync = async(type, ids) => api.getAsync(getRoiComparisonResourceUrl(type), { $filter: `id eq [${ids.join(',')}]` })
  const fetchCompanyTimelineByIdAsync = async(id, params = {}) => api.getCompanyTimelineByIdAsync(id, params)
  const fetchContactsByCompanyAsync = async(companyId) => api.getContactsAsync({ company_id: companyId })
  const fetchContactByIdAsync = async(id) => api.getAsync(`${api.contactsUrl}${id}/`)
  const fetchConversationsByCompanyAsync = async(companyId, params = {}) => api.getConversationsAsync({ company_id: companyId, ...params })
  const upsertContactAsync = async(data) => api.upsertAsync(api.contactsUrl, data)
  const deleteContactAsync = async(id, config) => api.deleteAsync(`${api.contactsUrl}${id}/`, config)
  const upsertConversationAsync = async(data) => api.upsertAsync(api.conversationsUrl, data)
  const deleteConversationAsync = async(id, config) => api.deleteAsync(`${api.conversationsUrl}${id}/`, config)
  const fetchEquipmentsAsync = async(params, store = false) => useFetchAndStoreAsync(equipments, () => api.getEquipmentsAsync(params), store)
  const fetchGroupsAsync = async(params, store = false) => useFetchAndStoreAsync(groups, () => api.getGroupsAsync(params), store)
  const fetchGroupByIdAsync = async(id) => api.getGroupByIdAsync(id)
  const fetchEquipmentByIdAsync = async(id, params = {}) => api.getEquipmentByIdAsync(id, params)
  const fetchEquipmentQrcodeByIdAsync = async(id, params = {}) => api.getEquipmentQrcodeByIdAsync(id, params)
  const fetchEquipmentStatsByIdAsync = async(id, params = {}) => api.getEquipmentStatsByIdAsync(id, params)
  const checkEquipmentSerializedAsync = async(id) => api.checkEquipmentSerializedAsync(id)
  const fetchEquipmentConditionsByEquipmentIdAsync = async(id, params = {}) => api.getEquipmentConditionsByEquipmentIdAsync(id, params)
  const fetchEquipmentSerialInstancesAsync = async(equipmentId) => api.getEquipmentSerialInstancesAsync(equipmentId)
  const addEquipmentSerialAsync = async(equipmentId, data) => api.addEquipmentSerialAsync(equipmentId, data)
  const deleteEquipmentSerialAsync = async(equipmentId, serialId) => api.deleteEquipmentSerialAsync(equipmentId, serialId)
  const fetchListingItemsAsync = async(params, store = false) => {
    if (!useWebSiteStore().canReadListings) return []
    return useFetchAndStoreAsync(listingItems, () => api.getListingItemsAsync(params), store)
  }
  const fetchListingItemByIdAsync = async(id, params = {}) => api.getListingItemByIdAsync(id, params)
  const fetchListingByIdAsync = async(id, params = {}) => api.getListingByIdAsync(id, params)
  const fetchListingsAsync = async(params, store = false) => {
    if (!useWebSiteStore().canReadListings) return []
    const res = await useFetchAndStoreAsync(listings, () => api.getListingsAsync(params), store)
    if (store) {
      listings.value = [...listings.value].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }
    return res
  }
  const fetchStaffsAsync = async(params, store = false) => useFetchAndStoreAsync(staff, () => api.getStaffsAsync(params), store)
  const fetchStaffByIdAsync = async(id, params = {}) => api.getStaffByIdAsync(id, params)
  const fetchProjectByIdAsync = async(id) => api.getProjectByIdAsync(id)
  const fetchProjectsAsync = async(params, store = false) => useFetchAndStoreAsync(projects, () => api.getProjectsAsync(params), store)
  const fetchServicesAsync = async(params) => api.getServicesAsync(params)
  const fetchProjectListingItemsByProjectIdAsync = async(projectId, params = {}) => api.getProjectListingItemsByProjectIdAsync(projectId, params)
  const fetchProjectListingItemServicesByProjectIdAsync = async(projectId, params = {}) => api.getProjectListingItemServicesByProjectIdAsync(projectId, params)
  const fetchProjectListingItemServicesInRangeAsync = async(params) => api.getProjectListingItemServicesAsync(params)
  const fetchEquipmentByUuidAsync = async(uuid) => api.getEquipmentByUuidAsync(uuid)

  const getProjectsAsync = async(params) => api.getProjectsAsync(params)

  const getCompanyChips = (company) => {
    const i18n = getI18n()
    const chips = []
    if (company.is_rental) {
      chips.push({
        name: i18n.t('common.rental'),
        label: i18n.t('common.rental'),
        color: 'grey',
        textColor: textColor('grey'),
        size: 'sm',
      })
    }
    if (company.is_client) {
      chips.push({
        name: i18n.t('common.client'),
        label: i18n.t('common.client'),
        color: 'green',
        textColor: textColor('green'),
        size: 'sm',
      })
    }
    if (company.archived) {
      chips.push({
        name: i18n.t('common.archived'),
        label: i18n.t('common.archived'),
        color: 'grey',
        textColor: textColor('grey'),
        size: 'sm',
      })
    }
    return chips
  }

  const getConditionLabel = (condition) => {
    const i18n = getI18n()
    return condition == null ? i18n.t('common.condition.unknown') : i18n.t(equipmentConditionEnumLabels[condition])
  }
  const getConditionColor = (condition) => condition == null ? 'grey' : equipmentConditionColors[condition]
  const getLocalizedEquipmentConditions = () => equipmentConditionEnumLabels.map((i, idx) => {
    const i18n = getI18n()
    const name = i18n.t(i)
    const color = getConditionColor(idx)
    return { id: idx, name: name, label: name, color: color, textColor: textColor(color) }
  })
  const getLocalizedServiceImportTargets = (serviceType) => serviceImportTargetEnumLabels.map((defaultLabel, idx) => {
    const label = (serviceType === portalStore.serviceTypeEnum.STAFF && staffImportTargetLabelOverrides[idx]) || defaultLabel
    const translated = getI18n().t(label, 1)
    return { id: idx, name: translated === label ? label.split('.').pop() : translated }
  })
  const getServiceImportNumberFormats = () => serviceImportNumberFormatExamples.map((example, idx) => ({ id: idx, name: example }))
  const getServiceImportDateFormats = () => serviceImportDateFormatExamples.map((example, idx) => ({ id: idx, name: example }))
  const getEquipmentById = (id) => equipments.value.find(equipment => equipment.id === +id)
  const getEventWarningTypeName = (type) => Object.keys(eventWarningTypeEnum)[type]?.toLocaleLowerCase()
  const getLocalizedProjectStatuses = () => projectStatusEnumLabels.map((i, idx) => {
    const i18n = getI18n()
    const name = i18n.t(i)
    const color = getProjectStatusColor(idx)
    return { id: idx, name: name, label: name, color: color, textColor: textColor(color) }
  })
  const getProjectById = (id) => projects.value.find(project => project.id === +id)
  const getProjectStatusColor = (status) => Object.values(projectStatusColorEnum)[status]
  const getProjectByIdAsync = async(id) => projects.value.find(project => project.id === +id) || await fetchProjectByIdAsync(id)
  const getListingItemName = (item) => {
    const listing = listings.value.find(i => i.id === item?.listing)
    return `${listing?.name} - ${item?.name}`
  }
  const getListings = () => {
    const { archiveDisplayExpr } = useArchive()
    return listings.value.map(i => {
      return {
        ...i,
        name: archiveDisplayExpr(i),
      }
    })
  }
  const getContactName = (contact) => [contact?.name, contact?.surname].filter(Boolean).join(' ')
  const getServiceName = (service, short = false, hideSerial = false) => {
    if (!service) {
      return ''
    }
    let name = service?.name
    if (service?.type === portalStore.serviceTypeEnum.STAFF) {
      name = `${service.name}`
      name += short ? ` ${service.first_name[0]}.` : ` ${service.first_name}`
    }
    const prefix = service?.code ? `${service.code} - ` : ''
    const serial = [service?.serial_number, service?.internal_serial].filter(Boolean).join(' / ')
    const serialSuffix = serial && !hideSerial ? ` (${serial})` : ''
    const i18n = getI18n()
    const rentedSuffix = service?.is_rented ? ` (${i18n.t('common.rented')})` : ''
    return `${prefix}${name}${serialSuffix}${rentedSuffix}`
  }
  const getGroupById = (id) => groups.value.find(group => group.id === +id)
  const getStaffById = (id) => staff.value.find(staff => staff.id === +id)
  const getServiceById = (id) => getStaffById(id) || getEquipmentById(id) || getGroupById(id)
  const getServices = () => [...staff.value, ...equipments.value, ...groups.value]

  const upsertCompanyAsync = withEntityChange([entityChangeTypeEnum.COMPANY], async(data) => api.upsertCompanyAsync(data))
  const upsertEquipmentAsync = withEntityChange([entityChangeTypeEnum.EQUIPMENT], async(data) => api.upsertEquipmentAsync(data))
  const fetchNextCompanyIdAsync = async() => (await api.getNextIdAsync(api.companiesUrl)).data.next_id
  const fetchNextListingIdAsync = async() => (await api.getNextIdAsync(api.listingsUrl)).data.next_id
  const fetchNextEquipmentIdAsync = async() => (await api.getNextIdAsync(api.equipmentsUrl)).data.next_id
  const fetchNextStaffIdAsync = async() => (await api.getNextIdAsync(api.staffsUrl)).data.next_id
  const fetchNextGroupIdAsync = async() => (await api.getNextIdAsync(api.groupsUrl)).data.next_id
  const upsertGroupAsync = withEntityChange([entityChangeTypeEnum.GROUP], async(data) => api.upsertGroupAsync(data))
  const upsertEquipmentConditionAsync = async(data) => api.upsertEquipmentConditionAsync(data)
  const upsertListingAsync = async(data) => api.upsertListingAsync(data)
  const upsertListingItemAsync = async(data) => api.upsertListingItemAsync(data)
  const upsertProjectAsync = withEntityChange([entityChangeTypeEnum.PROJECT], async(data) => api.upsertProjectAsync(data))
  const upsertProjectListingItemAsync = async(data) => api.upsertProjectListingItemAsync(data)
  const upsertProjectListingItemServiceAsync = async(data, params, config) => api.upsertProjectListingItemServiceAsync(data, params, config)
  const upsertStaffAsync = withEntityChange([entityChangeTypeEnum.STAFF], async(data) => api.upsertStaffAsync(data))
  const patchStaffAsync = (id, data) => api.patchAsync(`${api.staffsUrl}${id}/`, data)

  const archiveWarehouseAsync = async(id) => api.archiveWarehouseAsync(id)
  const zeroAndArchiveWarehouseAsync = async(id) => api.archiveWarehouseAsync(id, { params: { mode: warehouseArchiveModeEnum.ZERO_AND_ARCHIVE } })
  const deleteWarehouseAsync = async(id, config) => api.deleteWarehouseAsync(id, config)
  const fetchWarehousesAsync = async(params) => api.getWarehousesAsync(params)
  const fetchWarehouseByIdAsync = async(id) => api.getAsync(`${api.warehousesUrl}${id}/`)
  const fetchWarehouseMovementByIdAsync = async(id) => api.getAsync(`${api.warehouseMovementsUrl}${id}/`)
  const fetchWarehouseAvailabilityTimelineAsync = async(dateFrom, dateTo) =>
    api.getAsync(`${api.warehouseStocksUrl}timeline/`, { date_from: dateFrom, date_to: dateTo })
  const setWarehouseMinStockAsync = async(serviceId, warehouseId, quantity) =>
    api.putAsync(`${api.warehouseMinStocksUrl}set/`, { service: serviceId, warehouse: warehouseId, quantity })
  const upsertWarehouseAsync = async(data) => api.upsertWarehouseAsync(data)
  const fetchWarehouseMovementsAsync = async(params) => api.getWarehouseMovementsAsync(params)
  const upsertWarehouseMovementAsync = async(data) => api.upsertWarehouseMovementAsync(data)
  const deleteWarehouseMovementAsync = async(id, config) => api.deleteWarehouseMovementAsync(id, config)
  const confirmWarehouseMovementAsync = async(id, force, config) => api.confirmWarehouseMovementAsync(id, force, config)
  const revertWarehouseMovementAsync = async(id) => api.revertWarehouseMovementAsync(id)
  const getWarehouseMovementTwinAsync = async(id) => api.getWarehouseMovementTwinAsync(id)
  const fetchStockAvailabilityAsync = async(params) => api.getStockAvailabilityAsync(params)

  const $reset = () => {
    companies.value = []
    listings.value = []
    listingItems.value = []
    staff.value = []
    equipments.value = []
  }

  return {
    api,
    patchStaffAsync,
    addServiceTagAsync,
    addProjectTagAsync,
    companies,
    equipments,
    equipmentConditionColors,
    equipmentConditionEnum,
    equipmentConditionEnumLabels,
    roiComparisonTypeEnum,
    roiMetricFormatEnum,
    roiMetricBetterEnum,
    roiBarToneEnum,
    roiComparisonEditStepEnum,
    roiCompareBasket,
    getRoiComparisonResourceUrl,
    fetchRoiComparisonAsync,
    fetchRoiComparisonItemsAsync,
    serviceImportTargetEnum,
    serviceImportMultiColumnTargets,
    serviceImportCodeCollisionModeEnum,
    eventWarningTypeEnum,
    listings,
    listingItems,
    serviceTags,
    projectTags,
    projects,
    projectStatusEnum,
    projectTagKindEnum,
    referenceTypeEnum,
    contactResolveResultEnum,
    conversationTypeEnum,
    projectDeleteModeEnum,
    projectBoundsActionEnum,
    projectAllocationActionEnum,
    projectEditStepEnum,
    projectStatusColorEnum,
    projectStatusEnumLabels,
    groups,
    staff,

    archiveCompanyAsync,
    archiveEquipmentAsync,
    archiveGroupAsync,
    archiveListingAsync,
    archiveListingItemAsync,
    archiveProjectAsync,
    archiveStaffAsync,
    resendInviteStaffAsync,

    cloneEquipmentAsync,
    cloneGroupAsync,

    analyzeServiceImportAsync,
    importServicesAsync,
    bulkDeleteServicesAsync,
    exportServicesExcelAsync,
    cloneListingAsync,
    cloneListingItemAsync,

    deleteCompanyAsync,
    deleteEquipmentAsync,
    deleteEquipmentConditionAsync,
    deleteGroupAsync,
    deleteListingAsync,
    deleteListingItemAsync,
    deleteProjectAsync,
    deleteProjectListingItemAsync,
    deleteStaffAsync,

    downloadProjectFileAsync,
    downloadServiceFileAsync,
    printProjectDeliveryNoteAsync,
    previewProjectDeliveryNoteAsync,

    exportListingsAsync,
    exportProjectsExcelAsync,

    fetchCompaniesAsync,
    fetchCompanyByIdAsync,
    fetchServiceTagsAsync,
    fetchProjectTagsAsync,
    createProjectTagAsync,
    getLocalizedProjectTagKinds,
    fetchCompanyStatisticsByIdAsync,
    fetchCompanyRoiByIdAsync,
    fetchCompanyTimelineByIdAsync,
    fetchContactsByCompanyAsync,
    fetchContactByIdAsync,
    fetchConversationsByCompanyAsync,
    upsertContactAsync,
    deleteContactAsync,
    upsertConversationAsync,
    deleteConversationAsync,
    fetchEquipmentByIdAsync,
    fetchEquipmentByUuidAsync,
    fetchEquipmentStatsByIdAsync,
    checkEquipmentSerializedAsync,
    fetchEquipmentConditionsByEquipmentIdAsync,
    fetchEquipmentSerialInstancesAsync,
    addEquipmentSerialAsync,
    deleteEquipmentSerialAsync,
    fetchEquipmentQrcodeByIdAsync,
    fetchEquipmentsAsync,
    fetchGroupByIdAsync,
    fetchGroupsAsync,
    fetchListingItemsAsync,
    fetchListingItemByIdAsync,
    fetchListingByIdAsync,
    fetchListingsAsync,
    fetchProjectByIdAsync,
    fetchProjectListingItemsByProjectIdAsync,
    fetchProjectListingItemServicesByProjectIdAsync,
    fetchProjectListingItemServicesInRangeAsync,
    fetchProjectsAsync,
    fetchServicesAsync,
    fetchStaffByIdAsync,
    fetchStaffsAsync,

    getProjectsAsync,
    getCompanyChips,
    getLocalizedServiceTags,
    getLocalizedProjectTags,
    getConditionLabel,
    getConditionColor,
    getEquipmentById,
    getGroupById,
    getEventWarningTypeName,
    getServiceById,
    getServices,
    getListingItemName,
    getLocalizedEquipmentConditions,
    getLocalizedServiceImportTargets,
    getServiceImportNumberFormats,
    getServiceImportDateFormats,
    getLocalizedProjectStatuses,
    getListings,
    getProjectById,
    getProjectByIdAsync,
    getProjectStatusColor,
    getContactName,
    getServiceName,
    getStaffById,
    getVacationProject,
    isVacationProject,
    isUnitaryService,

    upsertCompanyAsync,
    upsertEquipmentAsync,
    fetchNextCompanyIdAsync,
    fetchNextListingIdAsync,
    fetchNextEquipmentIdAsync,
    fetchNextStaffIdAsync,
    fetchNextGroupIdAsync,
    upsertEquipmentConditionAsync,
    upsertListingAsync,
    upsertListingItemAsync,
    upsertProjectAsync,
    upsertProjectListingItemAsync,
    upsertProjectListingItemServiceAsync,
    upsertGroupAsync,
    upsertStaffAsync,

    warehouseMovementTypeEnum,
    warehouseKindEnum,
    warehouseMovementStatusEnum,
    warehouseAvailabilityStatusEnum,
    getLocalizedWarehouseMovementTypes,
    getLocalizedWarehouseMovementStatuses,
    getLocalizedStaffMovementTypes,
    warehouseArchiveModeEnum,
    archiveWarehouseAsync,
    zeroAndArchiveWarehouseAsync,
    deleteWarehouseAsync,
    fetchWarehousesAsync,
    fetchWarehouseByIdAsync,
    fetchWarehouseMovementByIdAsync,
    fetchWarehouseAvailabilityTimelineAsync,
    setWarehouseMinStockAsync,
    upsertWarehouseAsync,
    fetchWarehouseMovementsAsync,
    upsertWarehouseMovementAsync,
    deleteWarehouseMovementAsync,
    confirmWarehouseMovementAsync,
    revertWarehouseMovementAsync,
    getWarehouseMovementTwinAsync,
    fetchStockAvailabilityAsync,

    $reset,
  }
}, {
  persist: {
    enabled: true,
    strategies: [
      {
        storage: localStorage, paths: [
          'companies',
          'equipments',
          'listingItems',
          'listings',
          'serviceTags',
          'projectTags',
          'staff',
        ]
      },
      { storage: sessionStorage, paths: ['gridEditItemId'] }
    ],
  },
})
